import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { Base64 } from "js-base64";
import { find, get, head } from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { Fragment, useCallback, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import shallow from "zustand/shallow";
import ExternalLink from "../../../components/ExternalLink";
import DateInput from "../../../components/forms/DateInput";
import DropdownSelect, {
  DropdownSelectOption
} from "../../../components/forms/DropdownSelect";
import FormProvider from "../../../components/forms/FormProvider";
import Input from "../../../components/forms/Input";
import InfoIcon from "../../../components/icons/InfoIcon";
import QrCode from "../../../components/QrCode";
import { PAYMENT_MEMO_MAX_CHARACTERS } from "../../../context/commissionReports/HntPaymentSidebar";
import { useContacts } from "../../../context/contacts";
import { ContactPaymentContextKey } from "../../../context/contacts/ContactsProvider";
import { useViewport } from "../../../context/viewport";
import { useWorkspaces } from "../../../context/workspaces";
import { breakpoints } from "../../../utils/constants";
import { locale } from "../../../utils/dates";
import generateHeliumWalletDeepLink from "../../../utils/generateHeliumWalletDeepLink";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import { BONES_PER_HNT } from "../../../utils/useHntMarket";

export interface Props {
  contactUuid: string;
  open: boolean;
  toggleContactPaymentForm: () => void;
}

const AddContactPaymentFormSidebar: React.FC<Props> = ({
  contactUuid,
  open,
  toggleContactPaymentForm
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { contacts, addContactPayment, getDefaultPaymentMethod } =
    useContacts();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const { currentWorkspaceId } = useWorkspaces();
  const [currencyOptions, getCurrencyTitle] = useCurrency(
    (state) => [state.currencyOptions, state.getCurrencyTitle],
    shallow
  );
  const now = useRef(new Date());

  const currentContact = useMemo(
    () => contacts[contactUuid],
    [contactUuid, contacts]
  );

  const currentContactUuidToBase64 = useMemo(() => {
    const memo = currentContact?.uuid.substr(-PAYMENT_MEMO_MAX_CHARACTERS);

    return Base64.encode(memo);
  }, [currentContact?.uuid]);

  const defaultContactHntWallet: string = useMemo(() => {
    const paymentMethod = getDefaultPaymentMethod(contactUuid, CurrencyKey.hnt);

    if (!paymentMethod) return;

    const parsedMetadata = JSON.parse(paymentMethod.metadata);
    const walletAddress = get(parsedMetadata, "walletAddress");

    return walletAddress;
  }, [contactUuid, getDefaultPaymentMethod]);

  const onSubmit = useCallback(
    async (
      { currency, amount, paid_at, notes }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      if (!currentWorkspaceId) return;

      setErrorMessage(null);

      const apiResponse = await addContactPayment({
        currency: currency.key,
        amount:
          currency.key === CurrencyKey.hnt ? amount * BONES_PER_HNT : amount,
        paid_at: moment(paid_at).format("YYYY-MM-DD"),
        contact_uuid: contactUuid,
        workspace_uuid: currentWorkspaceId,
        report_commission_uuid: null,
        notes: notes || null,
        context: ContactPaymentContextKey.manual
      });

      if (!apiResponse?.status) {
        setErrorMessage(apiResponse?.message || "Something went wrong");
      } else {
        toggleContactPaymentForm();
      }

      formikHelpers.setSubmitting(false);
    },
    [
      addContactPayment,
      contactUuid,
      currentWorkspaceId,
      toggleContactPaymentForm
    ]
  );

  const renderHntPaymentMethod = useCallback(
    (formValues: FormValues) => {
      if (
        !defaultContactHntWallet ||
        formValues.currency.key !== CurrencyKey.hnt
      ) {
        return null;
      }

      if (formValues.amount <= 0) {
        return null;
      }

      if (!isMobile) {
        return (
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white">
              Pay contact {formValues.amount} HNT by QR code:
            </label>
            <div className="mt-1" style={{ height: "398px" }}>
              <QrCode
                size={398}
                background="#fff"
                fill="#A96CEB"
                text={getHntPaymentQr(
                  defaultContactHntWallet,
                  formValues.amount
                )}
                radius={0.5}
              />
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col">
          <h3 className="text-sm font-medium text-gray-400 mb-4">
            Pay contact {formValues.amount} HNT with the Helium Wallet
            <Tooltip
              overlay={
                <>
                  This will load the payment transaction in the Helium Wallet
                  App. Please ensure you have the{" "}
                  <ExternalLink
                    className="inline-flex"
                    text="iOS app"
                    url="https://apps.apple.com/br/app/helium-hnt-wallet/id1609525848?l=en"
                  />{" "}
                  or{" "}
                  <ExternalLink
                    className="inline-flex"
                    text="android app"
                    url="https://play.google.com/store/apps/details?id=com.helium.wallet.app"
                  />{" "}
                  installed before using this payment method.
                </>
              }
            >
              <InfoIcon className="ml-1 inline-flex h-4 w-4" />
            </Tooltip>
          </h3>

          <span
            className="inline-flex justify-center cursor-pointer py-2 px-1.5 md:px-4 border border-transparent shadow-sm text-xs md:text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
            onClick={() =>
              generateHeliumWalletDeepLink([
                {
                  amount: formValues.amount * BONES_PER_HNT,
                  payee: defaultContactHntWallet,
                  memo: currentContactUuidToBase64
                }
              ])
            }
          >
            Pay with Helium Wallet
          </span>
        </div>
      );
    },
    [currentContactUuidToBase64, defaultContactHntWallet, isMobile]
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={toggleContactPaymentForm}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <FormProvider
                  classnames={{ wrapper: "h-full" }}
                  onCancel={toggleContactPaymentForm}
                  error={errorMessage}
                  formikProps={{
                    onSubmit,
                    initialValues: {
                      currency: find(currencyOptions, {
                        key:
                          head(currentContact?.payment_methods)?.currency ||
                          CurrencyKey.hnt
                      }),
                      amount: 0,
                      paid_at: now.current,
                      notes: ""
                    },
                    validationSchema: Yup.object().shape({
                      currency: Yup.object().required("Required"),
                      amount: Yup.number().required("Required"),
                      paid_at: Yup.date().required("Required"),
                      notes: Yup.string().nullable()
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    values: formValues
                  }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              Register contact payment
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={toggleContactPaymentForm}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              {renderHntPaymentMethod(formValues)}

                              <Field
                                className="-mt-1"
                                name="currency"
                                label="Currency"
                                disabled={isSubmitting}
                                items={currencyOptions}
                                itemToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                optionToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                component={DropdownSelect}
                              />

                              <Field
                                id="amount"
                                name="amount"
                                label="Amount"
                                type="number"
                                component={Input}
                                disabled={isSubmitting}
                              />

                              <Field
                                id="paid_at"
                                name="paid_at"
                                label="Payment date"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                component={DateInput}
                                disabled={isSubmitting}
                              />

                              <Field
                                id="notes"
                                name="notes"
                                label="Notes"
                                placeholder="E.g. Compensation for broken cable during installation"
                                type="text"
                                textarea
                                rows={3}
                                component={Input}
                                disabled={isSubmitting}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddContactPaymentFormSidebar;

//
// Utils
//

interface FormValues {
  currency: DropdownSelectOption<CurrencyKey>;
  amount: number;
  notes: string;
  paid_at: string;
}

const ORDERED_FIELD_NAMES = ["currency", "amount", "paid_at", "notes"];

const getHntPaymentQr = (walletAddress: string, amount: number) =>
  JSON.stringify({
    type: "payment",
    address: walletAddress,
    amount: amount
  });
